// RotatingScripture — renders a Scripture verse pulled from the curated pool.
// The verse rotates per (key, mood) combination via localStorage so users
// see variety across visits instead of the same three verses repeatedly.
//
// Props:
//   scriptureKey — unique surface key ('welcome-hero', 'dashboard', 'login-hero'...)
//   mood         — optional filter: 'rest' | 'guidance' | 'peace' | 'courage'
//                  | 'purpose' | 'wisdom' | 'provision'
//   variant      — 'card' (bordered card) | 'inline' (simple stacked text)
//                  | 'compact' (small caption — mobile fallback)
//   center       — boolean, center-align the text (default true)
//   className    — extra wrapper class
//   verseClassName / referenceClassName — override text classes if needed

import React, { useMemo } from 'react';
import { getRotatingScripture } from '../utils/scriptures';

export const RotatingScripture = ({
  scriptureKey = 'default',
  mood = null,
  variant = 'card',
  center = true,
  className = '',
  verseClassName = '',
  referenceClassName = '',
  showQuotes = true,
}) => {
  // Compute once per mount so the verse doesn't flicker on re-renders.
  const scripture = useMemo(
    () => getRotatingScripture(scriptureKey, mood),
    [scriptureKey, mood]
  );

  const alignClass = center ? 'text-center' : '';

  if (variant === 'compact') {
    return (
      <div className={`${alignClass} ${className}`} data-testid={`rotating-scripture-${scriptureKey}`}>
        <p className={`font-scripture text-sm leading-relaxed mb-1 text-[var(--sd-muted-fg)] italic ${verseClassName}`}>
          {showQuotes ? `“${scripture.text}”` : scripture.text}
        </p>
        <p className={`text-xs text-[var(--sd-primary)] font-scripture font-semibold ${referenceClassName}`}>
          — {scripture.reference}
        </p>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`${alignClass} ${className}`} data-testid={`rotating-scripture-${scriptureKey}`}>
        <p className={`font-scripture italic text-base leading-relaxed text-[var(--sd-fg)] ${verseClassName}`}>
          {showQuotes ? `“${scripture.text}”` : scripture.text}
        </p>
        <p className={`text-xs text-[var(--sd-primary)] mt-2 font-scripture ${referenceClassName}`}>
          — {scripture.reference} ({scripture.translation})
        </p>
      </div>
    );
  }

  // Default: card
  return (
    <div className={`glass-card p-5 ${className}`} data-testid={`rotating-scripture-${scriptureKey}`}>
      <p className={`font-scripture text-base leading-relaxed ${alignClass} italic text-[var(--sd-fg)] ${verseClassName}`}>
        {showQuotes ? `“${scripture.text}”` : scripture.text}
      </p>
      <p className={`text-sm text-[var(--sd-primary-dark)] font-scripture font-semibold ${alignClass} mt-2 ${referenceClassName}`}>
        — {scripture.reference} ({scripture.translation})
      </p>
    </div>
  );
};

export default RotatingScripture;
